/** Sélecteur vertical partagé par les évaluations PHY et RPS. */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck } from "@fortawesome/free-solid-svg-icons"
import { PhysicalRiskScaleItem } from "@/entities/physical-risk/model/physical-risk.types"
import { getGradientColorAt, withAlpha } from "@/entities/physical-risk/ui/physical-risk-scale-colors.utils"

//Geometrie de l'echelle : le trait reliant les dots se deduit entierement de ces valeurs
const DOT_SIZE = 38
const ACTIVE_DOT_SIZE = 46
const DOT_MARGIN = 6 //Respiration autour du dot : donne l'ecart avec le fond de la ligne active
//Meme respiration a gauche du dot qu'au-dessus et en dessous, pour que le fond de la ligne active
//soit a egale distance du cercle sur ses trois cotes. La hauteur de ligne, elle, vient deja des
//marges de la colonne du dot : pas de min-height a ajouter.
const DOT_COLUMN_PADDING_LEFT = DOT_MARGIN
const LABEL_GAP = 21 //Espace entre le dot et son libelle
//Largeur derivee : le libelle garde sa distance au dot quand la respiration autour du dot change
const DOT_COLUMN_WIDTH = DOT_COLUMN_PADDING_LEFT + ACTIVE_DOT_SIZE + LABEL_GAP
const ROW_GAP = 6
const CONNECTOR_WIDTH = 14
const ACTIVE_ROW_ALPHA = 0.12 //Opacite du fond de la ligne selectionnee

interface Props {
  scale: readonly PhysicalRiskScaleItem[]
  value: number | null
  showValues?: boolean
  onChange?: (value: number) => void
}

export default function RiskScale({ scale, value, showValues = false, onChange }: Props) {
  if (!scale.length) return null
  const scaleLength = scale.length
  const maxScaleValue = scale[scaleLength - 1]?.value ?? 1
  const activePoint = value === null ? null : Math.min(value, maxScaleValue)
  const activeIndex = scale.findIndex((item) => item.value === activePoint)
  const activeColor = getGradientColorAt(activeIndex === -1 ? 0 : activeIndex / Math.max(1, scaleLength - 1))

  function getScalePointStyle(color: string, isActive: boolean) {
    let backgroundColor = ""
    const borderColor = color

    if (isActive) {
      backgroundColor = color
      color = "white"
    }

    return {
      color,
      borderColor,
      backgroundColor
    }
  }

  function renderScaleRow(scaleItem: PhysicalRiskScaleItem, i: number) {
    const rowIsReadonly = !onChange
    const position = i / Math.max(1, scaleLength - 1)
    const color = getGradientColorAt(position)
    const isActive = scaleItem.value === activePoint
    const nextColor = i < scaleLength - 1 ? getGradientColorAt((i + 1) / Math.max(1, scaleLength - 1)) : null
    const pointSize = isActive ? ACTIVE_DOT_SIZE : DOT_SIZE
    const pointOffset = (ACTIVE_DOT_SIZE - pointSize) / 2
    const nextIsActive = scale[i + 1]?.value === activePoint
    const nextPointSize = nextIsActive ? ACTIVE_DOT_SIZE : DOT_SIZE
    const nextPointOffset = (ACTIVE_DOT_SIZE - nextPointSize) / 2

    return (
      <button
        key={scaleItem.value}
        type="button"
        className={`${rowIsReadonly ? "" : "_hover"} group relative flex w-full items-center bg-transparent p-0 text-left`}
        style={{
          //overflow : la regle globale "button { overflow: hidden }" clipperait le trait
          backgroundColor: isActive ? withAlpha(color, ACTIVE_ROW_ALPHA) : undefined,
          border: "none",
          borderRadius: 12,
          overflow: "visible",
          paddingRight: isActive ? 14 : undefined
        }}
        aria-pressed={isActive}
        onClick={() => onChange?.(scaleItem.value)}>
        {/* Trait reliant ce dot a celui de la ligne suivante. Ancre par top ET bottom pour suivre la
            hauteur reelle de la ligne, qui varie selon la ligne active et les libelles sur deux lignes */}
        {nextColor && (
          <div
            style={{
              background: `linear-gradient(to bottom, ${color}, ${nextColor})`,
              bottom: -(ROW_GAP + DOT_MARGIN + nextPointOffset),
              left: DOT_COLUMN_PADDING_LEFT + (ACTIVE_DOT_SIZE - CONNECTOR_WIDTH) / 2,
              position: "absolute",
              top: DOT_MARGIN + pointOffset + pointSize,
              width: CONNECTOR_WIDTH
            }}
          />
        )}
        <div
          className="relative shrink-0"
          style={{
            height: ACTIVE_DOT_SIZE + 2 * DOT_MARGIN,
            isolation: "isolate",
            overflow: "visible",
            paddingLeft: DOT_COLUMN_PADDING_LEFT,
            width: DOT_COLUMN_WIDTH
          }}>
          <div
            className="flex transform-gpu items-center justify-center transition-transform duration-200 ease-out group-hover:scale-[1.2]"
            style={{
              ...getScalePointStyle(color, isActive),
              boxSizing: "border-box",
              borderStyle: "solid",
              borderWidth: 3,
              borderRadius: 12,
              fontSize: 18,
              height: pointSize,
              marginLeft: pointOffset,
              marginTop: DOT_MARGIN + pointOffset,
              minHeight: pointSize,
              minWidth: pointSize,
              position: "relative",
              width: pointSize,
              zIndex: 2
            }}>
            {showValues ? scaleItem.value : isActive ? <FontAwesomeIcon icon={faCheck} /> : null}
          </div>
        </div>
        {isActive && showValues && (
          <FontAwesomeIcon className="mr-10 shrink-0 text-base" icon={faCheck} style={{ color: activeColor }} />
        )}
        <div className="min-w-0 flex-1">
          <div className={isActive ? "font-bold" : undefined}>{scaleItem.label}</div>
          {/* Les echelles enregistrees avant l'ajout du champ n'ont pas de precision */}
          {scaleItem.description && <div className="grey-t text-sm leading-snug">{scaleItem.description}</div>}
        </div>
      </button>
    )
  }

  return (
    <div className="flex flex-col" style={{ gap: ROW_GAP }}>
      {scale.map((scaleItem, i) => renderScaleRow(scaleItem, i))}
    </div>
  )
}
